/**
 * Generato automaticamente.
 * framework-geocoding
 */
import { useQuery, useMutation } from 'react-query';
import type {
  UseQueryOptions,
  UseMutationOptions,
  QueryFunction,
  MutationFunction,
  UseQueryResult,
  QueryKey,
} from 'react-query';
import type { SimpleFeatureCollection } from '../schemas/simpleFeatureCollection.ts';
import type { PostGeocodingConvertShapefileToGeojsonBody } from '../schemas/postGeocodingConvertShapefileToGeojsonBody.ts';
import type { NormalizzazioneIndirizzoDTO } from '../schemas/normalizzazioneIndirizzoDTO.ts';
import type { GetGeocodingSuggerimentiParams } from '../schemas/getGeocodingSuggerimentiParams.ts';
import type { ParticelleCatastaliDTO } from '../schemas/particelleCatastaliDTO.ts';
import type { GetGeocodingParticelleCatastaliParams } from '../schemas/getGeocodingParticelleCatastaliParams.ts';
import type { CoordinateIndirizzoDTO } from '../schemas/coordinateIndirizzoDTO.ts';
import type { GetGeocodingCoordinateIndirizzoParams } from '../schemas/getGeocodingCoordinateIndirizzoParams.ts';
import { apiClientRequest } from '../../../client/apiClient.ts';
import type { ErrorType } from '../../../client/apiClient.ts';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

export const postGeocodingConvertShapefileToGeojson = (
  postGeocodingConvertShapefileToGeojsonBody: PostGeocodingConvertShapefileToGeojsonBody,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<SimpleFeatureCollection>(
    {
      url: `/framework-geocoding/geocoding/convert/shapefile-to-geojson`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: postGeocodingConvertShapefileToGeojsonBody,
    },
    options,
  );
};

export const getPostGeocodingConvertShapefileToGeojsonMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postGeocodingConvertShapefileToGeojson>>,
    TError,
    { data: PostGeocodingConvertShapefileToGeojsonBody },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postGeocodingConvertShapefileToGeojson>>,
  TError,
  { data: PostGeocodingConvertShapefileToGeojsonBody },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postGeocodingConvertShapefileToGeojson>>,
    { data: PostGeocodingConvertShapefileToGeojsonBody }
  > = props => {
    const { data } = props ?? {};

    return postGeocodingConvertShapefileToGeojson(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostGeocodingConvertShapefileToGeojsonMutationResult = NonNullable<
  Awaited<ReturnType<typeof postGeocodingConvertShapefileToGeojson>>
>;
export type PostGeocodingConvertShapefileToGeojsonMutationBody =
  PostGeocodingConvertShapefileToGeojsonBody;
export type PostGeocodingConvertShapefileToGeojsonMutationError =
  ErrorType<unknown>;

export const usePostGeocodingConvertShapefileToGeojson = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postGeocodingConvertShapefileToGeojson>>,
    TError,
    { data: PostGeocodingConvertShapefileToGeojsonBody },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostGeocodingConvertShapefileToGeojsonMutationOptions(options);

  return useMutation(mutationOptions);
};
export const postGeocodingConvertGeojsonToShapefile = (
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<string[]>(
    {
      url: `/framework-geocoding/geocoding/convert/geojson-to-shapefile`,
      method: 'post',
    },
    options,
  );
};

export const getPostGeocodingConvertGeojsonToShapefileMutationOptions = <
  TError = ErrorType<unknown>,
  TVariables = void,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postGeocodingConvertGeojsonToShapefile>>,
    TError,
    TVariables,
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postGeocodingConvertGeojsonToShapefile>>,
  TError,
  TVariables,
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postGeocodingConvertGeojsonToShapefile>>,
    TVariables
  > = () => {
    return postGeocodingConvertGeojsonToShapefile(requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostGeocodingConvertGeojsonToShapefileMutationResult = NonNullable<
  Awaited<ReturnType<typeof postGeocodingConvertGeojsonToShapefile>>
>;

export type PostGeocodingConvertGeojsonToShapefileMutationError =
  ErrorType<unknown>;

export const usePostGeocodingConvertGeojsonToShapefile = <
  TError = ErrorType<unknown>,
  TVariables = void,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postGeocodingConvertGeojsonToShapefile>>,
    TError,
    TVariables,
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostGeocodingConvertGeojsonToShapefileMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Ottiene oggetto contenente l'elenco dei suggerimenti di indirizzi normalizzati
 * @summary Elenco indirizzi normalizzati dato un indirizzo non normalizzato
 */
export const getGeocodingSuggerimenti = (
  params: GetGeocodingSuggerimentiParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<NormalizzazioneIndirizzoDTO>(
    {
      url: `/framework-geocoding/geocoding/suggerimenti`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetGeocodingSuggerimentiQueryKey = (
  params: GetGeocodingSuggerimentiParams,
) =>
  [
    `/framework-geocoding/geocoding/suggerimenti`,
    ...(params ? [params] : []),
  ] as const;

export const getGetGeocodingSuggerimentiQueryOptions = <
  TData = Awaited<ReturnType<typeof getGeocodingSuggerimenti>>,
  TError = ErrorType<unknown>,
>(
  params: GetGeocodingSuggerimentiParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getGeocodingSuggerimenti>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getGeocodingSuggerimenti>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetGeocodingSuggerimentiQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getGeocodingSuggerimenti>>
  > = ({ signal }) => getGeocodingSuggerimenti(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetGeocodingSuggerimentiQueryResult = NonNullable<
  Awaited<ReturnType<typeof getGeocodingSuggerimenti>>
>;
export type GetGeocodingSuggerimentiQueryError = ErrorType<unknown>;

/**
 * @summary Elenco indirizzi normalizzati dato un indirizzo non normalizzato
 */
export const useGetGeocodingSuggerimenti = <
  TData = Awaited<ReturnType<typeof getGeocodingSuggerimenti>>,
  TError = ErrorType<unknown>,
>(
  params: GetGeocodingSuggerimentiParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getGeocodingSuggerimenti>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetGeocodingSuggerimentiQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Coordinate particelle catastali
 * @summary Coordinate particelle catastali
 */
export const getGeocodingParticelleCatastali = (
  params: GetGeocodingParticelleCatastaliParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<ParticelleCatastaliDTO>(
    {
      url: `/framework-geocoding/geocoding/particelle-catastali`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetGeocodingParticelleCatastaliQueryKey = (
  params: GetGeocodingParticelleCatastaliParams,
) =>
  [
    `/framework-geocoding/geocoding/particelle-catastali`,
    ...(params ? [params] : []),
  ] as const;

export const getGetGeocodingParticelleCatastaliQueryOptions = <
  TData = Awaited<ReturnType<typeof getGeocodingParticelleCatastali>>,
  TError = ErrorType<unknown>,
>(
  params: GetGeocodingParticelleCatastaliParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getGeocodingParticelleCatastali>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getGeocodingParticelleCatastali>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetGeocodingParticelleCatastaliQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getGeocodingParticelleCatastali>>
  > = ({ signal }) =>
    getGeocodingParticelleCatastali(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetGeocodingParticelleCatastaliQueryResult = NonNullable<
  Awaited<ReturnType<typeof getGeocodingParticelleCatastali>>
>;
export type GetGeocodingParticelleCatastaliQueryError = ErrorType<unknown>;

/**
 * @summary Coordinate particelle catastali
 */
export const useGetGeocodingParticelleCatastali = <
  TData = Awaited<ReturnType<typeof getGeocodingParticelleCatastali>>,
  TError = ErrorType<unknown>,
>(
  params: GetGeocodingParticelleCatastaliParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getGeocodingParticelleCatastali>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetGeocodingParticelleCatastaliQueryOptions(
    params,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Ottiene le coordinate geografiche dato un indirizzo normalizzato
 * @summary Coordinate indirizzo normalizzato
 */
export const getGeocodingCoordinateIndirizzo = (
  params?: GetGeocodingCoordinateIndirizzoParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<CoordinateIndirizzoDTO>(
    {
      url: `/framework-geocoding/geocoding/coordinate-indirizzo`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetGeocodingCoordinateIndirizzoQueryKey = (
  params?: GetGeocodingCoordinateIndirizzoParams,
) =>
  [
    `/framework-geocoding/geocoding/coordinate-indirizzo`,
    ...(params ? [params] : []),
  ] as const;

export const getGetGeocodingCoordinateIndirizzoQueryOptions = <
  TData = Awaited<ReturnType<typeof getGeocodingCoordinateIndirizzo>>,
  TError = ErrorType<unknown>,
>(
  params?: GetGeocodingCoordinateIndirizzoParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getGeocodingCoordinateIndirizzo>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getGeocodingCoordinateIndirizzo>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetGeocodingCoordinateIndirizzoQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getGeocodingCoordinateIndirizzo>>
  > = ({ signal }) =>
    getGeocodingCoordinateIndirizzo(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetGeocodingCoordinateIndirizzoQueryResult = NonNullable<
  Awaited<ReturnType<typeof getGeocodingCoordinateIndirizzo>>
>;
export type GetGeocodingCoordinateIndirizzoQueryError = ErrorType<unknown>;

/**
 * @summary Coordinate indirizzo normalizzato
 */
export const useGetGeocodingCoordinateIndirizzo = <
  TData = Awaited<ReturnType<typeof getGeocodingCoordinateIndirizzo>>,
  TError = ErrorType<unknown>,
>(
  params?: GetGeocodingCoordinateIndirizzoParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getGeocodingCoordinateIndirizzo>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetGeocodingCoordinateIndirizzoQueryOptions(
    params,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
